import React, { useEffect, useState } from "react";
import i18n from "../../i18n/config";
import { FORM_NAMES } from "../../constants/forms";
import { BANK_ACCOUNT_FORM_FIELDS, FIELD_TYPES } from "../../constants/enums";
import { useDispatch, useSelector } from "react-redux";
import { reduxForm, InjectedFormProps, reset, change, untouch } from "redux-form";
import { BankAccountChildProps } from "../../types/props";
import { CountryType, CurrencyType } from "../../types/api/catalog";
import { DropdownType } from "../../types/fields";
import { useHistory } from "react-router-dom";

import DynamicInputField from "../../components/Common/DynamicFields/DynamicInputField";
import { ROUTES } from "../../constants/clientRoutes";
import { SetSelectedCountry, SetSelectedCurrency } from "../../actions/bankAccounts";
import useFetchCountryCurrencyOptions from "../../hooks/bankAccounts/CountryCurrencySelection/useFetchCountryCurrencyOptions";
import { RootState } from "../../types/redux";

const CountryCurrencySelection: React.FC<BankAccountChildProps & InjectedFormProps<{}, BankAccountChildProps>> = (props: BankAccountChildProps) => {
  const { setTitle, setSubTitle, setForwardButton } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [countryOptions, setCountryOptions] = useState<DropdownType[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<DropdownType[]>([]);
  const { availableCurrencies } = useSelector((state: RootState) => state.bankAccounts);
  const forwardButtonInitialState = { text: i18n.t("PAGES.BANK_ACCOUNTS.COUNTRY_CURRENCY_SELECTION.BUTTON_TEXT"), clickFunc: () => history.push(ROUTES.BANKS_ACCOUNTS.ACCOUNT_DETAILS), disabled: true };
  useFetchCountryCurrencyOptions(setCountryOptions, setCountries);

  useEffect(() => {
    setTitle(i18n.t("PAGES.BANK_ACCOUNTS.COUNTRY_CURRENCY_SELECTION.TITLE"));
    setSubTitle(i18n.t("PAGES.BANK_ACCOUNTS.COUNTRY_CURRENCY_SELECTION.SUBTITLE"));
    setForwardButton(forwardButtonInitialState);
    dispatch(reset(FORM_NAMES.BANK_ACCOUNTS));
  }, []);

  const handleCountryChange = (value: string) => {
    const selectedCountry = countries.find((country: CountryType) => String(country.id) === value);
    if (selectedCountry) {
      setCurrencyOptions((selectedCountry.currencies ?? []).map((currency) => ({ label: currency.name, value: String(currency.id) })));
      dispatch(SetSelectedCountry(selectedCountry.currencies ?? [], selectedCountry.name));

      // Clear selected currency value in redux form and disable forward button when country is changed
      dispatch(change(FORM_NAMES.BANK_ACCOUNTS, BANK_ACCOUNT_FORM_FIELDS.DESTINATION_CURRENCY, ""));
      dispatch(untouch(FORM_NAMES.BANK_ACCOUNTS, BANK_ACCOUNT_FORM_FIELDS.DESTINATION_CURRENCY));
      setForwardButton({
        ...forwardButtonInitialState,
        disabled: true
      });
    }
  }

  const handleCurrencyChange = (value: string) => {
    const disabled = value === "";
    setForwardButton({
      ...forwardButtonInitialState,
      disabled
    });
    const selectedCurrency = availableCurrencies.find((currency: CurrencyType) => String(currency.id) === value);
    if (selectedCurrency) {
      dispatch(SetSelectedCurrency(selectedCurrency.label, selectedCurrency.code));
    }
  };

  return (
    <form className="spacing-top-medium">
      <div className="max-w-360">
        <DynamicInputField
          id={BANK_ACCOUNT_FORM_FIELDS.DESTINATION_COUNTRY}
          type={FIELD_TYPES.SELECT}
          label={i18n.t("PAGES.BANK_ACCOUNTS.COUNTRY_CURRENCY_SELECTION.COUNTRY_LABEL")}
          required={true}
          focus={true}
          options={countryOptions}
          onChange={(value: string) => handleCountryChange(value)}
        />

        <DynamicInputField
          id={BANK_ACCOUNT_FORM_FIELDS.DESTINATION_CURRENCY}
          type={FIELD_TYPES.SELECT}
          label={i18n.t("PAGES.BANK_ACCOUNTS.COUNTRY_CURRENCY_SELECTION.CURRENCY_LABEL")}
          required={true}
          focus={true}
          options={currencyOptions}
          onChange={handleCurrencyChange}
          disabled={currencyOptions.length === 0}
        />
      </div>
    </form>
  );
}

export default reduxForm<{}, BankAccountChildProps>({
  form: FORM_NAMES.BANK_ACCOUNTS,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true
})(CountryCurrencySelection);